import React, { useState, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Stack,
    Typography,
    Button,
    Radio,
    RadioGroup,
    FormControlLabel,
    Divider,
    Chip,
} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import EventIcon from '@mui/icons-material/Event';
import { formatDateForDisplay } from '@moment-golf/shared';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface LinkedEventOption {
    _id: string;
    eventName: string;
    startDate: string;
    endDate: string;
}

interface CalculateThroughDialogProps {
    open: boolean;
    onClose: () => void;
    /** Called with the selected targetEventID when the user confirms */
    onConfirm: (targetEventID: string) => void;
    linkedEvents: LinkedEventOption[];
    factor: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDateRange(event: LinkedEventOption): string {
    const opts = { year: '2-digit' as const, month: 'numeric' as const, day: 'numeric' as const };
    const startStr = new Date(event.startDate).toLocaleDateString('en-US', { timeZone: 'UTC' });
    const endStr   = new Date(event.endDate).toLocaleDateString('en-US', { timeZone: 'UTC' });
    if (startStr === endStr) return formatDateForDisplay(event.startDate, opts);
    return `${formatDateForDisplay(event.startDate, opts)} – ${formatDateForDisplay(event.endDate, opts)}`;
}

/** Returns the _id of the last event whose endDate ≤ today (UTC), or the first event if none are past. */
function resolveDefaultEventID(events: LinkedEventOption[]): string | null {
    if (events.length === 0) return null;
    const now = new Date();
    // Find last event (highest index in endDate-sorted list) that has already ended
    const past = events.filter((e) => new Date(e.endDate) <= now);
    if (past.length > 0) return past[past.length - 1]._id;
    // All events are future — default to the earliest one
    return events[0]._id;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const CalculateThroughDialog: React.FC<CalculateThroughDialogProps> = ({
    open,
    onClose,
    onConfirm,
    linkedEvents,
    factor,
}) => {
    // Sort ascending by endDate so the list reads chronologically
    const sortedEvents = useMemo(
        () => [...linkedEvents].sort((a, b) => new Date(a.endDate).getTime() - new Date(b.endDate).getTime()),
        [linkedEvents],
    );

    const defaultID = useMemo(() => resolveDefaultEventID(sortedEvents), [sortedEvents]);
    const [selectedID, setSelectedID] = useState<string | null>(defaultID);

    // Reset selection whenever the dialog opens with potentially new events
    React.useEffect(() => {
        if (open) setSelectedID(resolveDefaultEventID(sortedEvents));
    }, [open, sortedEvents]);

    const now = new Date();

    const handleConfirm = () => {
        if (selectedID) onConfirm(selectedID);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth='sm'
            fullWidth
            PaperProps={{ sx: { backgroundColor: 'sepia.background' } }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Stack direction='row' alignItems='center' spacing={1}>
                    <CalculateIcon sx={{ fontSize: 18 * factor, color: 'text.secondary' }} />
                    <Typography sx={{ fontSize: 14 * factor, fontWeight: 700 }}>
                        Calculate Standings Through…
                    </Typography>
                </Stack>
                <Typography variant='caption' sx={{ fontSize: 11 * factor, color: 'text.secondary', display: 'block', mt: 0.5 }}>
                    Choose the last event to include. Only events up to and including the selected event's end date will count.
                </Typography>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ pt: 1.5, pb: 1 }}>
                {sortedEvents.length === 0 ? (
                    <Typography variant='body2' sx={{ fontSize: 12 * factor, color: 'text.secondary' }}>
                        No events are linked to this collection.
                    </Typography>
                ) : (
                    <RadioGroup
                        value={selectedID ?? ''}
                        onChange={(_, val) => setSelectedID(val)}
                    >
                        {sortedEvents.map((ev) => {
                            const isCompleted = new Date(ev.endDate) <= now;
                            const isDefault = ev._id === defaultID;
                            return (
                                <FormControlLabel
                                    key={ev._id}
                                    value={ev._id}
                                    control={<Radio size='small' sx={{ py: 0.5 }} />}
                                    label={
                                        <Stack direction='row' alignItems='center' spacing={1} sx={{ py: 0.25 }}>
                                            <EventIcon sx={{ fontSize: 13 * factor, color: 'text.disabled' }} />
                                            <Box>
                                                <Stack direction='row' alignItems='center' spacing={0.75}>
                                                    <Typography sx={{ fontSize: 12 * factor, fontWeight: 600 }}>
                                                        {ev.eventName}
                                                    </Typography>
                                                    {isDefault && (
                                                        <Chip
                                                            label='Default'
                                                            size='small'
                                                            color='primary'
                                                            variant='outlined'
                                                            sx={{ fontSize: 9 * factor, height: 16 * factor, px: 0.25 }}
                                                        />
                                                    )}
                                                    {!isCompleted && (
                                                        <Chip
                                                            label='Upcoming'
                                                            size='small'
                                                            variant='outlined'
                                                            sx={{ fontSize: 9 * factor, height: 16 * factor, px: 0.25, color: 'text.secondary' }}
                                                        />
                                                    )}
                                                </Stack>
                                                <Typography variant='caption' sx={{ fontSize: 10 * factor, color: 'text.secondary' }}>
                                                    {formatDateRange(ev)}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    }
                                    sx={{ mx: 0, px: 1, py: 0.25, borderRadius: 1,
                                        '&:hover': { backgroundColor: 'action.hover' },
                                    }}
                                />
                            );
                        })}
                    </RadioGroup>
                )}
            </DialogContent>

            <Divider />

            <DialogActions sx={{ px: 2.5, py: 1.5 }}>
                <Button
                    variant='text'
                    size='small'
                    onClick={onClose}
                    sx={{ fontSize: 12 * factor, textTransform: 'none', color: 'text.secondary' }}
                >
                    Cancel
                </Button>
                <Button
                    variant='contained'
                    size='small'
                    onClick={handleConfirm}
                    disabled={!selectedID || sortedEvents.length === 0}
                    startIcon={<CalculateIcon sx={{ fontSize: 14 * factor }} />}
                    sx={{ fontSize: 12 * factor, textTransform: 'none' }}
                >
                    Calculate Standings
                </Button>
            </DialogActions>
        </Dialog>
    );
};
